<template>
  <div>
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
      <div class="flex-1">
        <input v-model="search" @input="onSearchInput" type="text" placeholder="Search by name, email, subject, or message..." class="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" />
      </div>
      <button @click="fetchContacts" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition">Refresh</button>
    </div>
    <div v-if="loading" class="text-center py-8 text-lg text-gray-500">Loading...</div>
    <div v-else>
      <div class="mb-2 text-sm text-gray-600">Total: {{ total }} | Page: {{ page }} / {{ totalPages }}</div>
      <table class="min-w-full bg-white rounded shadow overflow-x-auto">
        <thead>
          <tr class="bg-gray-100 text-left">
            <th class="py-3 px-4">Name</th>
            <th class="py-3 px-4">Email</th>
            <th class="py-3 px-4">Subject</th>
            <th class="py-3 px-4">Message</th>
            <th class="py-3 px-4">Date</th>
            <th class="py-3 px-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="contact in contacts" :key="contact.id" class="border-b hover:bg-gray-50">
            <td class="py-2 px-4">{{ contact.name }}</td>
            <td class="py-2 px-4">{{ contact.email }}</td>
            <td class="py-2 px-4">{{ contact.subject }}</td>
            <td class="py-2 px-4 max-w-xs truncate" :title="contact.message">{{ contact.message }}</td>
            <td class="py-2 px-4">{{ formatDate(contact.created_at) }}</td>
            <td class="py-2 px-4 flex gap-2">
              <button @click="openEdit(contact)" class="px-2 py-1 text-xs bg-yellow-400 text-white rounded hover:bg-yellow-500">Edit</button>
              <button @click="openDelete(contact)" class="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
            </td>
          </tr>
          <tr v-if="contacts.length === 0">
            <td colspan="6" class="text-center py-8 text-gray-400">No contact submissions found.</td>
          </tr>
        </tbody>
      </table>
      <div v-if="error" class="text-red-600 mt-4">{{ error }}</div>
      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-6">
        <button @click="goToPage(page - 1)" :disabled="page === 1" class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50">Prev</button>
        <button v-for="p in totalPages" :key="p" @click="goToPage(p)" :class="['px-3 py-1 rounded', p === page ? 'bg-primary text-white' : 'bg-gray-200 hover:bg-gray-300']">{{ p }}</button>
        <button @click="goToPage(page + 1)" :disabled="page === totalPages" class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50">Next</button>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEdit" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-8 w-full max-w-md shadow-lg relative">
        <h2 class="text-xl font-bold mb-4">Edit Contact</h2>
        <form @submit.prevent="submitEdit">
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Name</label>
            <input v-model="editForm.name" class="w-full border rounded px-3 py-2" required />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Email</label>
            <input v-model="editForm.email" class="w-full border rounded px-3 py-2" required type="email" />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Subject</label>
            <input v-model="editForm.subject" class="w-full border rounded px-3 py-2" required />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Message</label>
            <textarea v-model="editForm.message" class="w-full border rounded px-3 py-2" required rows="3"></textarea>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button type="button" @click="closeEdit" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">Save</button>
          </div>
          <div v-if="editError" class="text-red-600 mt-2">{{ editError }}</div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div v-if="showDelete" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-8 w-full max-w-sm shadow-lg relative">
        <h2 class="text-lg font-bold mb-4">Delete Contact</h2>
        <p>Are you sure you want to delete this contact?</p>
        <div class="flex justify-end gap-2 mt-6">
          <button @click="closeDelete" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
          <button @click="confirmDelete" class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
        </div>
        <div v-if="deleteError" class="text-red-600 mt-2">{{ deleteError }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance } from 'vue'

const { appContext } = getCurrentInstance()
const apiBaseUrl = appContext.config.globalProperties.$apiBaseUrl

const contacts = ref([])
const loading = ref(false)
const error = ref(null)
const total = ref(0)
const page = ref(1)
const limit = ref(10)
const search = ref('')
const totalPages = ref(1)
let searchTimeout = null

const showEdit = ref(false)
const editForm = ref({ id: null, name: '', email: '', subject: '', message: '' })
const editError = ref('')

const showDelete = ref(false)
const deleteContact = ref(null)
const deleteError = ref('')

const fetchContacts = async () => {
  loading.value = true
  error.value = null
  try {
    const params = new URLSearchParams({
      q: search.value,
      page: page.value,
      limit: limit.value
    })
    const response = await fetch(`${apiBaseUrl}/api/contact?${params.toString()}`)
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to fetch contacts')
    contacts.value = data.contacts || []
    total.value = data.total || 0
    page.value = data.page || 1
    limit.value = data.limit || 10
    totalPages.value = Math.ceil(total.value / limit.value)
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const goToPage = (p) => {
  if (p < 1 || p > totalPages.value) return
  page.value = p
  fetchContacts()
}

const onSearchInput = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    fetchContacts()
  }, 400)
}

const openEdit = (contact) => {
  editForm.value = { ...contact }
  editError.value = ''
  showEdit.value = true
}
const closeEdit = () => {
  showEdit.value = false
}
const submitEdit = async () => {
  editError.value = ''
  try {
    const response = await fetch(`${apiBaseUrl}/api/contact/${editForm.value.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm.value)
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to update contact')
    showEdit.value = false
    await fetchContacts()
  } catch (err) {
    editError.value = err.message
  }
}

const openDelete = (contact) => {
  deleteContact.value = contact
  deleteError.value = ''
  showDelete.value = true
}
const closeDelete = () => {
  showDelete.value = false
}
const confirmDelete = async () => {
  deleteError.value = ''
  try {
    const response = await fetch(`${apiBaseUrl}/api/contact/${deleteContact.value.id}`, {
      method: 'DELETE'
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to delete contact')
    showDelete.value = false
    await fetchContacts()
  } catch (err) {
    deleteError.value = err.message
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleString()
}

onMounted(fetchContacts)
watch(page, fetchContacts)
</script>

<style scoped>
table {
  @apply w-full;
}
th, td {
  @apply text-sm;
}
</style> 